// Professional Print Panel - Implements expert requirements
// Simple and intuitive GUI for event operators

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Printer,
  Settings,
  Image,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  Pause,
  RotateCcw,
  Trash2,
  Eye,
  Download,
  Wifi,
  WifiOff,
  FileText,
  Layers,
  Zap,
  Shield,
  RefreshCcw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import advancedPrintService from '../../services/advancedPrintService';

export default function ProfessionalPrintPanel() {
  const [isConnected, setIsConnected] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [printQueue, setPrintQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [printerCapabilities, setPrinterCapabilities] = useState(null);

  // Print settings
  const [printSettings, setPrintSettings] = useState({
    size: '4x6',
    quality: 'standard',
    copies: 1,
    paperType: 'glossy',
    border: true,
    logo: true,
    autoPrint: false,
    colorCorrection: 'auto',
    brightness: 0,
    contrast: 0
  });

  // Professional print sizes
  const printSizes = [
    { value: '4x6', label: '4x6" (10x15 ס"מ)', aspect: '2:3' },
    { value: '5x7', label: '5x7" (13x18 ס"מ)', aspect: '5:7' },
    { value: '6x8', label: '6x8" (15x20 ס"מ)', aspect: '3:4' },
    { value: '8x10', label: '8x10" (20x25 ס"מ)', aspect: '4:5' }
  ];

  // Quality options
  const qualityOptions = [
    { value: 'standard', label: 'רגיל', description: 'איכות סטנדרטית' },
    { value: 'high', label: 'גבוה', description: 'איכות גבוהה' },
    { value: 'ultra', label: 'אולטרה', description: 'איכות מקסימלית' }
  ];

  // Paper types
  const paperTypes = [
    { value: 'glossy', label: 'מבריק', description: 'גימור מבריק' },
    { value: 'matte', label: 'מט', description: 'גימור מט' },
    { value: 'satin', label: 'סאטן', description: 'גימור סאטן' }
  ];

  useEffect(() => {
    initializePrintService();
    return () => {
      advancedPrintService.disconnect();
    };
  }, []);

  const initializePrintService = async () => {
    try {
      // Set up event listeners
      advancedPrintService.on('connected', () => {
        setIsConnected(true);
        advancedPrintService.detectPrinters();
      });

      advancedPrintService.on('disconnected', () => {
        setIsConnected(false);
      });

      advancedPrintService.on('printers', (detectedPrinters) => {
        setPrinters(detectedPrinters.supported || []);
        if (detectedPrinters.supported?.length > 0 && !selectedPrinter) {
          setSelectedPrinter(detectedPrinters.supported[0].name);
        }
      });

      advancedPrintService.on('printStatus', (status) => {
        updatePrintQueue(status);
      });

      advancedPrintService.on('jobCompleted', (job) => {
        updatePrintQueue({ jobId: job.id, status: 'completed' });
      });

      advancedPrintService.on('jobFailed', (job) => {
        updatePrintQueue({ jobId: job.id, status: 'failed', error: job.error });
      });

      advancedPrintService.on('jobProgress', (job) => {
        updatePrintQueue({ jobId: job.id, progress: job.progress });
      });

      advancedPrintService.on('printError', (errorData) => {
        console.error('Print error:', errorData);
        // Show error notification
      });

      // Connect to print server
      await advancedPrintService.connect();
    } catch (error) {
      console.error('Failed to initialize print service:', error);
    }
  };

  const updatePrintQueue = (update) => {
    setPrintQueue(prev => {
      return prev.map(job => {
        if (job.id === update.jobId) {
          return { ...job, ...update };
        }
        return job;
      });
    });
  };

  const handlePrint = (imageData) => {
    const jobId = advancedPrintService.printImage(imageData, {
      ...printSettings,
      printerName: selectedPrinter,
      eventName: 'AI Photo Booth Event',
      template: 'default'
    });

    // Add to local queue for UI
    setPrintQueue(prev => [...prev, {
      id: jobId,
      imageData,
      status: 'queued',
      timestamp: new Date(),
      settings: { ...printSettings }
    }]);
  };

  const handleCancelJob = (jobId) => {
    advancedPrintService.cancelJob(jobId);
    setPrintQueue(prev => prev.filter(job => job.id !== jobId));
  };

  const handleRetryJob = (jobId) => {
    const job = printQueue.find(j => j.id === jobId);
    if (job) {
      advancedPrintService.retryJob(jobId);
    }
  };

  const handlePreview = (job) => {
    setSelectedJob(job);
    setPreviewImage(job.imageData);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'queued':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'processing':
        return <Play className="w-4 h-4 text-blue-500 animate-pulse" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'timeout':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'queued': return 'ממתין';
      case 'processing': return 'מדפיס';
      case 'completed': return 'הודפס';
      case 'failed': return 'נכשל';
      case 'timeout': return 'פג תוקף';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4 space-x-reverse">
            <h1 className="text-3xl font-bold text-white">מערכת הדפסה מקצועית</h1>
            <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
              isConnected ? 'bg-green-500/20 border-green-500/50' : 'bg-red-500/20 border-red-500/50'
            } border`}>
              {isConnected ? (
                <Wifi className="w-4 h-4 text-green-500" />
              ) : (
                <WifiOff className="w-4 h-4 text-red-500" />
              )}
              <span className={`text-sm font-medium ${
                isConnected ? 'text-green-500' : 'text-red-500'
              }`}>
                {isConnected ? 'מחובר' : 'מנותק'}
              </span>
            </div>
          </div>

          <Button
            onClick={() => setShowSettings(!showSettings)}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <Settings className="w-4 h-4 ml-2" />
            הגדרות
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Print Queue */}
          <div className="lg:col-span-2 space-y-6">
            {/* Printer Selection */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-white mb-4">בחירת מדפסת</h2>
                
                {printers.length > 0 ? (
                  <div className="grid gap-3">
                    {printers.map((printer) => (
                      <div
                        key={printer.name}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedPrinter === printer.name
                            ? 'border-violet-500 bg-violet-500/10'
                            : 'border-slate-600 bg-slate-900/50 hover:border-slate-500'
                        }`}
                        onClick={() => setSelectedPrinter(printer.name)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 space-x-reverse">
                            <Printer className="w-5 h-5 text-violet-500" />
                            <div>
                              <div className="font-medium text-white">{printer.name}</div>
                              <div className="text-sm text-slate-400">{printer.model || 'Unknown Model'}</div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline" className="border-green-500/50 text-green-500">
                              נתמך
                            </Badge>
                            {selectedPrinter === printer.name && (
                              <CheckCircle className="w-5 h-5 text-violet-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <Printer className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>לא נמצאו מדפסות נתמכות</p>
                    <p className="text-sm">ודא שהמדפסת מחוברת ומופעלת</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Print Queue */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white">תור הדפסה</h2>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="border-blue-500/50 text-blue-500">
                      {printQueue.length} משימות
                    </Badge>
                    {isProcessing && (
                      <Badge variant="outline" className="border-orange-500/50 text-orange-500">
                        מעבד
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <AnimatePresence>
                    {printQueue.map((job) => (
                      <motion.div
                        key={job.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="p-4 bg-slate-900/50 rounded-lg border border-slate-700"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 space-x-reverse">
                            {getStatusIcon(job.status)}
                            <div>
                              <div className="font-medium text-white">
                                הדפסה #{job.id.slice(-6)}
                              </div>
                              <div className="text-sm text-slate-400">
                                {job.settings?.size || '4x6'} • {job.copies || 1} עותקים
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-slate-400">
                              {getStatusText(job.status)}
                            </span>
                            
                            {job.progress && (
                              <div className="w-16 bg-slate-700 rounded-full h-2">
                                <div
                                  className="bg-violet-500 h-2 rounded-full transition-all"
                                  style={{ width: `${job.progress}%` }}
                                />
                              </div>
                            )}
                            
                            <div className="flex space-x-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePreview(job)}
                                className="text-slate-400 hover:text-white"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              
                              {job.status === 'failed' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRetryJob(job.id)}
                                  className="text-orange-400 hover:text-orange-300"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                              
                              {(job.status === 'queued' || job.status === 'failed') && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleCancelJob(job.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {job.error && (
                          <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400">
                            {job.error.message || 'שגיאת הדפסה'}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {printQueue.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>תור ההדפסה ריק</p>
                    <p className="text-sm">התמונות יופיעו כאן לאחר הצילום</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Settings Panel */}
          <div className="space-y-6">
            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-6"
                >
                  {/* Print Settings */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-4">הגדרות הדפסה</h3>
                      
                      <div className="space-y-4">
                        {/* Print Size */}
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            גודל הדפסה
                          </label>
                          <Select value={printSettings.size} onValueChange={(value) => 
                            setPrintSettings(prev => ({ ...prev, size: value }))
                          }>
                            <SelectTrigger className="bg-slate-900 border-slate-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {printSizes.map((size) => (
                                <SelectItem key={size.value} value={size.value}>
                                  {size.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Quality */}
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            איכות
                          </label>
                          <Select value={printSettings.quality} onValueChange={(value) => 
                            setPrintSettings(prev => ({ ...prev, quality: value }))
                          }>
                            <SelectTrigger className="bg-slate-900 border-slate-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {qualityOptions.map((quality) => (
                                <SelectItem key={quality.value} value={quality.value}>
                                  <div>
                                    <div>{quality.label}</div>
                                    <div className="text-xs text-slate-400">{quality.description}</div>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Paper Type */}
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            סוג נייר
                          </label>
                          <Select value={printSettings.paperType} onValueChange={(value) => 
                            setPrintSettings(prev => ({ ...prev, paperType: value }))
                          }>
                            <SelectTrigger className="bg-slate-900 border-slate-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {paperTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  <div>
                                    <div>{type.label}</div>
                                    <div className="text-xs text-slate-400">{type.description}</div>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Copies */}
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            עותקים: {printSettings.copies}
                          </label>
                          <Slider
                            value={[printSettings.copies]}
                            onValueChange={([value]) => 
                              setPrintSettings(prev => ({ ...prev, copies: value }))
                            }
                            max={5}
                            min={1}
                            step={1}
                            className="w-full"
                          />
                        </div>

                        {/* Toggle Options */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-slate-300">
                              מסגרת
                            </label>
                            <Switch
                              checked={printSettings.border}
                              onCheckedChange={(checked) => 
                                setPrintSettings(prev => ({ ...prev, border: checked }))
                              }
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-slate-300">
                              לוגו
                            </label>
                            <Switch
                              checked={printSettings.logo}
                              onCheckedChange={(checked) => 
                                setPrintSettings(prev => ({ ...prev, logo: checked }))
                              }
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-slate-300">
                              הדפסה אוטומטית
                            </label>
                            <Switch
                              checked={printSettings.autoPrint}
                              onCheckedChange={(checked) => 
                                setPrintSettings(prev => ({ ...prev, autoPrint: checked }))
                              }
                            />
                          </div>
                        </div>

                        {/* Advanced Settings */}
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              בהירות: {printSettings.brightness}
                            </label>
                            <Slider
                              value={[printSettings.brightness]}
                              onValueChange={([value]) => 
                                setPrintSettings(prev => ({ ...prev, brightness: value }))
                            }
                              min={-20}
                              max={20}
                              step={1}
                              className="w-full"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              ניגוד: {printSettings.contrast}
                            </label>
                            <Slider
                              value={[printSettings.contrast]}
                              onValueChange={([value]) => 
                                setPrintSettings(prev => ({ ...prev, contrast: value }))
                              }
                              min={-20}
                              max={20}
                              step={1}
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Quick Actions */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-4">פעולות מהירות</h3>
                      
                      <div className="space-y-3">
                        <Button
                          variant="outline"
                          className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                          onClick={() => advancedPrintService.clearQueue()}
                        >
                          <Trash2 className="w-4 h-4 ml-2" />
                          נקה תור
                        </Button>

                        <Button
                          variant="outline"
                          className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                          onClick={() => advancedPrintService.getPrinters()}
                        >
                          <RefreshCcw className="w-4 h-4 ml-2" />
                          רענן מדפסות
                        </Button>

                        <Button
                          variant="outline"
                          className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                          onClick={() => window.open('/test-print', '_blank')}
                        >
                          <FileText className="w-4 h-4 ml-2" />
                          דף הדפסת ניסיון
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Status Card */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">סטטוס מערכת</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">חיבור</span>
                    <Badge variant={isConnected ? "default" : "destructive"}>
                      {isConnected ? "מחובר" : "מנותק"}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">מדפסת פעילה</span>
                    <Badge variant="outline">
                      {selectedPrinter || "לא נבחרה"}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">משימות בתור</span>
                    <Badge variant="outline">
                      {printQueue.length}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">סטטוס עיבוד</span>
                    <Badge variant={isProcessing ? "default" : "secondary"}>
                      {isProcessing ? "מעבד" : "ממתין"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Preview Modal */}
        <AnimatePresence>
          {previewImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
              onClick={() => setPreviewImage(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="relative max-w-4xl max-h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={previewImage}
                  alt="Print Preview"
                  className="w-full h-full object-contain rounded-lg"
                />
                
                <Button
                  className="absolute top-4 right-4"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewImage(null)}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
